/**
 * Stage B/F — the ONE wording of a vehicle's location. Components must not
 * invent their own: "In the lot" and "On site, spot unknown" are different
 * facts, and a planned bay always says "Planned:".
 *
 * Item #13 — technicians read people's words, never the model's: no location
 * kind, no "(removed)", no "Exact Spot Unknown" as if it were a place. Each
 * state has a short LABEL ("Spot 12"), a SENTENCE ("It's in Spot 12.") and a
 * FROM phrase that reads inside "Moving … from <from> to Bay 4".
 */
import type {
  CurrentVehicleLocation,
  OffSiteReason,
  VehicleLocationPlan,
} from './model';

export interface LocationNames {
  /** bay id → { name, assigned technician's name if any } */
  bays?: ReadonlyMap<string, { name: string; techName?: string | null }>;
  /** parking spot id → label ("12") */
  spots?: ReadonlyMap<string, { label: string }>;
}

const OFF_SITE_LABEL: Record<OffSiteReason, string> = {
  TEST_DRIVE: 'Test drive',
  SUBLET: 'Sublet',
  OTHER: 'Other',
};

export function bayLabel(bayId: string | null, names: LocationNames = {}): string {
  if (!bayId) return 'Bay';
  const bay = names.bays?.get(bayId);
  if (!bay) return 'A bay that is no longer listed';
  return bay.techName ? `${bay.name} — ${bay.techName}'s Bay` : bay.name;
}

export function parkingSpotLabel(spotId: string | null, names: LocationNames = {}): string {
  if (!spotId) return 'Spot';
  const spot = names.spots?.get(spotId);
  return spot ? `Spot ${spot.label}` : 'A spot that is no longer listed';
}

/** One state, three ways to say it. */
export interface LocationWords {
  /** "Spot 12", "In the lot", "Parked, spot not recorded" */
  label: string;
  /** "It's in Spot 12." — a whole sentence */
  sentence: string;
  /** reads after "from": "Spot 12", "the lot", "somewhere on site" */
  from: string;
}

/** The no-row case Guided Start may still move from (a dependent ticket's legacy "parked"). */
export const PARKED_NOT_RECORDED: LocationWords = {
  label: 'Parked, spot not recorded',
  sentence: "It's parked, but OptiBay hasn't recorded where.",
  from: "where it's parked",
};

const NOTHING_RECORDED: LocationWords = {
  label: 'Location not recorded',
  sentence: "OptiBay hasn't recorded where it is.",
  from: 'where it is',
};

function lowerFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * Where the vehicle is, in people's words. `parkedNotRecorded`: no row, but the
 * vehicle is known to be parked — never more precise than that.
 */
export function describeLocation(
  loc: CurrentVehicleLocation | null | undefined,
  names: LocationNames = {},
  opts: { parkedNotRecorded?: boolean } = {},
): LocationWords {
  if (!loc) return opts.parkedNotRecorded ? PARKED_NOT_RECORDED : NOTHING_RECORDED;
  switch (loc.kind) {
    case 'BAY': {
      if (loc.bayId && names.bays?.has(loc.bayId)) {
        const label = bayLabel(loc.bayId, names);
        return { label, sentence: `It's in ${label}.`, from: label };
      }
      return loc.bayId
        ? { label: bayLabel(loc.bayId, names), sentence: `It's in ${lowerFirst(bayLabel(loc.bayId, names))}.`, from: 'its bay' }
        : { label: 'In a bay', sentence: "It's in a bay.", from: 'its bay' };
    }
    case 'PARKING_SPOT': {
      const listed = !!(loc.parkingSpotId && names.spots?.has(loc.parkingSpotId));
      const label = parkingSpotLabel(loc.parkingSpotId, names);
      return listed
        ? { label, sentence: `It's in ${label}.`, from: label }
        : { label: 'Parked in a spot', sentence: "It's parked in a spot.", from: 'its parking spot' };
    }
    case 'ON_SITE_GENERAL':
      return { label: 'In the lot', sentence: "It's in the lot (no specific spot).", from: 'the lot' };
    case 'ON_SITE_UNKNOWN_EXACT':
      return { label: 'On site, spot unknown', sentence: "It's on site, but we don't know exactly where.", from: 'somewhere on site' };
    case 'OFF_SITE': {
      // "Other" says nothing a person needs; a real reason is said naturally.
      const reason = loc.offSiteReason && loc.offSiteReason !== 'OTHER' ? OFF_SITE_LABEL[loc.offSiteReason] : null;
      return reason
        ? { label: `Off site — ${reason}`, sentence: `It's off site — ${reason}.`, from: 'off site' }
        : { label: 'Off site', sentence: "It's off site.", from: 'off site' };
    }
    case 'NOT_PRESENT':
      // Core-100 R2 CORE-X: NOT_PRESENT is recorded only when a car LEAVES (pickup, or an
      // administrator clearing its place) — a known absence. It is not "not yet arrived":
      // a car never located has no row at all and reads "Location not recorded".
      return { label: 'Not at the shop', sentence: "It isn't at the shop — it was recorded as having left.", from: 'outside the shop' };
    default: {
      const never: never = loc.kind;
      return { label: String(never), sentence: String(never), from: String(never) };
    }
  }
}

/** "Where is it now?" — e.g. "Bay 4 — Josh's Bay", "Spot 12", "In the lot". */
export function formatCurrentLocation(
  loc: CurrentVehicleLocation | null | undefined,
  names: LocationNames = {},
): string {
  return describeLocation(loc, names).label;
}

/** "Where is it planned to go?" — always prefixed, never mistakable for now. */
export function formatPlannedLocation(
  plan: VehicleLocationPlan | null | undefined,
  names: LocationNames = {},
): string | null {
  if (!plan) return null;
  const where = plan.kind === 'BAY' ? bayLabel(plan.bayId, names) : parkingSpotLabel(plan.parkingSpotId, names);
  return `Planned: ${where}`;
}
